"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const specs = [{
  label: "Experience",
  value: "20+ Years Expertise"
}, {
  label: "Materials",
  value: "Premium Grade Slate & Metal"
}, {
  label: "Warranty",
  value: "Lifetime Workmanship"
}, {
  label: "Response",
  value: "24/7 Emergency Repair"
}, {
  label: "Standards",
  value: "SABS & Industry Certified"
}];
export default function PrecisionInMotion() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const [maskProgress, setMaskProgress] = useState(0);
  const maskProgressRef = useRef(0);
  const maskDirectionRef = useRef(1);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    const animate = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx || canvas.width === 0 || canvas.height === 0) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gridSize = 48;
      const time = Date.now() * 0.0004;
      const offsetX = time * 20 % gridSize;
      const offsetY = time * 10 % gridSize;
      ctx.strokeStyle = "hsla(222, 47%, 11%, 0.04)";
      ctx.lineWidth = 1;
      for (let x = -gridSize + offsetX; x < canvas.width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = -gridSize + offsetY; y < canvas.height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);
  useEffect(() => {
    let raf: number;
    const speed = 0.004;
    const loop = () => {
      maskProgressRef.current += speed * maskDirectionRef.current;
      if (maskProgressRef.current >= 1) {
        maskProgressRef.current = 1;
        maskDirectionRef.current = -1;
      } else if (maskProgressRef.current <= 0) {
        maskProgressRef.current = 0;
        maskDirectionRef.current = 1;
      }
      setMaskProgress(maskProgressRef.current);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return <section data-section-id="232138" id="shielding-your-property-from-the-elements" className="relative overflow-hidden bg-background text-foreground min-h-screen flex flex-col">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

      {}
      <div className="absolute top-0 left-0 right-0 h-1" style={{
      background: "var(--primary)"
    }} />

      <div className="relative z-10 flex flex-col lg:flex-row flex-1 min-h-screen">
        {}
        <div ref={leftRef} className="flex flex-col justify-center w-full ![padding:0px_94px_0px_96px] !h-[1190px] lg:w-[60%]" style={{
        opacity: leftVisible ? 1 : 0,
        transform: leftVisible ? "translateX(0)" : "translateX(-40px)",
        transition: "opacity 0.8s ease, transform 0.8s ease"
      }}>
          {}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-px" style={{
            background: "hsl(38, 92%, 50%)"
          }} />
            <span className="font-mono text-xs tracking-widest uppercase" style={{
            color: "var(--muted-foreground)"
          }}>RM ROOFING &amp; WATERPROOFING — EST. 2025</span>
          </div>

          {}
          <h1 className="font-default text-5xl md:text-6xl xl:text-7xl font-extrabold leading-none tracking-tighter mb-8" style={{
          color: "var(--primary)"
        }}>
            SHIELDING
            <br />
            YOUR PROPERTY,
            <br />
            <span className="relative inline-block" style={{
            color: "hsl(38, 92%, 50%)"
          }}>
              PROTECTION.
              <span className="absolute -bottom-1 left-0 h-1 w-full" style={{
              background: "hsl(38, 92%, 50%)",
              opacity: 0.3
            }} />
            </span>
          </h1>

          {}
          <p className="font-default text-base md:text-lg mb-10 max-w-lg leading-relaxed" style={{
          color: "var(--muted-foreground)"
        }}>
            Trusted providers of high-quality roofing and waterproofing solutions. We protect residential and commercial properties with architectural precision and industrial-grade durability.
          </p>

          {}
          <div className="mb-10 p-5 border rounded-lg shadow-sm" style={{
          borderColor: "var(--border)",
          background: "var(--card)"
        }}>
            <div className="text-xs font-mono tracking-widest uppercase mb-4" style={{
            color: "var(--muted-foreground)"
          }}>
              TECHNICAL SPECIFICATIONS
            </div>
            <ul className="space-y-2">
              {specs.map((spec, i) => <li data-index={i} key={spec.label} className="flex items-center justify-between gap-4">
                  <span className="font-mono text-xs" style={{
                color: "var(--muted-foreground)"
              }}>
                    {spec.label}
                  </span>
                  <div className="flex-1 h-px opacity-20" style={{
                background: "var(--border)"
              }} />
                  <span className="font-mono text-xs font-bold" style={{
                color: "var(--primary)"
              }}>
                    {spec.value}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/get-a-quote">
              <Button size="lg" className="gap-3 px-8 py-6 text-base font-default font-bold tracking-wide rounded-full group shadow-lg" style={{
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              border: "none"
            }}>
                GET A FREE QUOTE
                <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
              </Button>
            </Link>
          </div>

          {}
          <div className="flex flex-wrap gap-6 mt-10">
            {["SABS Certified", "24/7 Support", "Industrial Grade"].map((badge, i) => <div data-index={i} key={badge} className="flex items-center gap-2">
                <CircleCheck size={14} style={{
              color: "hsl(38, 92%, 50%)"
            }} />
                <span className="font-mono text-xs" style={{
              color: "var(--muted-foreground)"
            }}>
                  {badge}
                </span>
              </div>)}
          </div>
        </div>

        {}
        <div ref={rightRef} className="relative flex items-center justify-center w-full lg:w-[40%] min-h-[420px] lg:min-h-screen overflow-hidden" style={{
        opacity: rightVisible ? 1 : 0,
        transform: rightVisible ? "translateY(0)" : "translateY(40px)",
        transition: "opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s"
      }}>
          {}
          <div className="absolute inset-0" style={{
          background: "var(--secondary)"
        }} />

          {}
          <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 47px, var(--primary) 47px, var(--primary) 48px), repeating-linear-gradient(90deg, transparent, transparent 47px, var(--primary) 47px, var(--primary) 48px)`
        }} />

          {}
          <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2" style={{
          borderColor: "hsl(38, 92%, 50%)"
        }} />
          <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2" style={{
          borderColor: "hsl(38, 92%, 50%)"
        }} />
          <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2" style={{
          borderColor: "var(--border)",
          opacity: 0.4
        }} />
          <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2" style={{
          borderColor: "var(--border)",
          opacity: 0.4
        }} />

          {}
          <div className="relative flex flex-col items-center gap-6 p-8 z-10">
            <div className="relative w-64 h-64 md:w-72 md:h-72 xl:w-80 xl:h-80">
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645726/img/roofing_texture_base.png" alt="Premium Roofing Texture" className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-2xl" style={{
              filter: "grayscale(20%) contrast(110%)"
            }} />
              {}
              <div className="absolute inset-0 w-full h-full rounded-lg" style={{
              backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645726/img/waterproofing_seal_layer.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              WebkitMaskImage: `linear-gradient(to bottom, rgba(0,0,0,1) ${maskProgress * 100}%, rgba(0,0,0,0) ${maskProgress * 100 + 10}%)`,
              maskImage: `linear-gradient(to bottom, rgba(0,0,0,1) ${maskProgress * 100}%, rgba(0,0,0,0) ${maskProgress * 100 + 10}%)`,
              mixBlendMode: "multiply",
              opacity: 0.8
            }} />
            </div>

            {}
            <div className="w-48 flex flex-col items-center gap-2">
              <div className="flex justify-between w-full">
                <span className="font-mono text-xs" style={{
                color: "var(--muted-foreground)"
              }}>
                  SEAL INTEGRITY
                </span>
                <span className="font-mono text-xs font-bold" style={{
                color: "var(--primary)"
              }}>
                  {Math.round(maskProgress * 100)}%
                </span>
              </div>
              <div className="w-full h-1 rounded-full overflow-hidden" style={{
              background: "var(--secondary)"
            }}>
                <div className="h-full transition-none" style={{
                width: `${maskProgress * 100}%`,
                background: "hsl(38, 92%, 50%)"
              }} />
              </div>
            </div>

            {}
            <div className="px-6 py-3 border-2 font-default font-bold text-xs tracking-wider uppercase rounded-full shadow-md" style={{
            borderColor: "var(--primary)",
            color: "var(--primary)",
            background: "var(--card)"
          }}>
                   TRUSTED QUALITY
            </div>
          </div>
        </div>
      </div>

      </section>;
}